import { inject, Injectable } from '@angular/core';
import { TableSavedSettings, TableSettings } from '@shared/interfaces/table/table-saved-settings';
import { TablePageSize } from '@shared/interfaces/table/table-pagesize';
import { SortParam } from '@app/core/constants/sort-param';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class TableConfigService {
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly PREFIX = 't-';

  /**
   * Generate the full storage key with prefix
   */
  private getStorageKey(tableId: string): string {
    return `${this.PREFIX}${tableId}`;
  }

  public fetchSettings(tableId: string): TableSavedSettings {
    return JSON.parse(localStorage.getItem(this.getStorageKey(tableId)) ?? '{}');
  }

  /**
   * Save a singular setting for a key in a specific table
   * @param tableId - Table identifier (without prefix)
   * @param key - Setting key
   * @param settings - Settings to save
   */
  public saveSetting(tableId: string, key: string, settings: TableSettings): void {
    const s = this.fetchSettings(tableId) ?? { config: {} };

    if (!(s?.config)) {
      s.config = {};
    }

    s.config[key] = settings;
    this.saveSettings(tableId, s);
  }

  public saveSettings(tableId: string, settings: TableSavedSettings): void {
    localStorage.setItem(this.getStorageKey(tableId), JSON.stringify(settings));
  }

  public updateSettings(tableId: string, settings: TableSavedSettings): void {
    const currentSettings = this.fetchSettings(tableId);
    this.saveSettings(tableId, { ...currentSettings, ...settings });
  }

  public removeSettings(tableId: string): void {
    localStorage.removeItem(this.getStorageKey(tableId));
  }

  public getFromKey(tableId: string, key: string): TableSettings {
    const settings = this.fetchSettings(tableId);
    return settings.config ? settings.config[key] : {};
  }

  public getColumns(tableId: string): string[] {
    const settings = this.fetchSettings(tableId);
    return settings.columns || [];
  }

  public removeColumnSettings(tableId: string): void {
    const settings = this.fetchSettings(tableId);
    delete settings.columns;
    this.saveSettings(tableId, settings);
  }

  public reorderColumns(tableId: string, columns: string[]): void {
    const settings = this.fetchSettings(tableId);
    settings.columns = columns;
    this.saveSettings(tableId, settings);
  }

  public getPageSize(tableId: string): TablePageSize | undefined {
    const settings = this.fetchSettings(tableId);
    return settings.pageSize;
  }

  public updatePageSize(tableId: string, pageSize: TablePageSize): void {
    const settings = this.fetchSettings(tableId);
    settings.pageSize = pageSize;
    this.saveSettings(tableId, settings);
  }

  public getSorting(tableId: string): SortParam | undefined {
    const settings = this.fetchSettings(tableId);
    return settings.sort;
  }

  public updateSorting(tableId: string, sort: SortParam): void {
    const settings = this.fetchSettings(tableId);
    settings.sort = sort;
    this.saveSettings(tableId, settings);
  }

  public updateTitle(tableId: string, title: string): void {
    const settings = this.fetchSettings(tableId);
    settings.title = this.translateService.instant(title);
    this.saveSettings(tableId, settings);
  }

  public getTitle(tableId: string): string {
    const settings = this.fetchSettings(tableId);
    return settings.title ?? tableId;
  }

  /**
   * Fetch all table configurations from localStorage
   *
   * @returns Array of table IDs and their settings (IDs without prefix)
   */
  public fetchAll(): { tableId: string; settings: TableSavedSettings }[] {
    const allTables: { tableId: string; settings: TableSavedSettings }[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key?.startsWith(this.PREFIX)) {
        const tableId = key.substring(this.PREFIX.length); // Remove prefix
        const settings = this.fetchSettings(tableId);
        allTables.push({ tableId, settings });
      }
    }

    return allTables;
  }

  public resetTable(tableId: string): void {
    localStorage.removeItem(this.getStorageKey(tableId));
  }

  /**
   * Remove all table configurations from localStorage
   */
  public removeAll(): void {
    const keysToRemove: string[] = [];

    // Collect all keys to remove
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.PREFIX)) {
        keysToRemove.push(key);
      }
    }

    // Remove all collected keys
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  /**
   * Get all table IDs (without prefix)
   *
   * @returns Array of table IDs
   */
  public getAllTableIds(): string[] {
    const tableIds: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.PREFIX)) {
        tableIds.push(key.substring(this.PREFIX.length));
      }
    }

    return tableIds;
  }
}
